import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import core, { API, App, View } from '@gin-betting/core';
import filters from '@gin-betting/filters';
import { Map as ImmutableMap } from 'immutable';
import { concat, includes, intersection, union, without } from 'lodash';
import { createStructuredSelector } from 'reselect';

import {
    BettingWidgets,
    OUTRIGHTS_ONLY_SPORTS,
    SKIP_MATCH_LIST_INITIAL_REQUEST,
} from 'app-constants';
import { useWidgetSettings, useWithLocation } from 'hooks';
import type { State } from 'types';
import { checkSkipPollerConfiguration, getRouteBySportType } from 'utils';
import { useOutrightsCountBySport } from '../../../hooks';
import {
    getCategorizerVars,
    getSortedCategorizerItems,
    getSportsWithCheckedTournaments,
    getSummaryPath,
} from '../../../utils';

const {
    constants: { SportType },
    poller: { usePoller },
    utils: {
        getAppliedFilters,
        getSportTypeBySportTags,
        getSportTypeFromLocation,
        checkOnRoute,
    },
    entities: {
        selectors: { selectSports },
    },
    services: { stringify, getHistory },
    hooks: { useDeepMemo },
} = core;

const {
    actions: { getCategorizer },
    selectors: { selectCategorizerData },
} = filters;

interface Input {
    selectedSportIds: string[];
    onFilterChange: (sportIds: string[], tournamentIds?: string[]) => void;
}

interface Output {
    outrightsCountBySport: Record<string, number>;
    sortedCategorizerItems: API.CategorizerItem[];
    handleSportSelect: (categorizerItem: API.CategorizerItem) => void;
    handleTournamentIdsChange: (
        tournamentIds: string[],
        sportIdToRemove: string
    ) => void;
}

const selectProps = createStructuredSelector({
    sports: selectSports,
    categorizerItems: selectCategorizerData,
});

const useCategorizerBody = ({
    selectedSportIds,
    onFilterChange,
}: Input): Output => {
    const history = useHistory();
    const { sportsOrder } = useWidgetSettings({
        widget: BettingWidgets.CATEGORIZER,
    });
    const vars = useWithLocation(() => getCategorizerVars());
    const variables = useDeepMemo(vars);

    const { location } = history;
    const { pathname } = location;
    const { sports, categorizerItems } = useSelector<
        State,
        ReturnType<typeof selectProps>
    >(state => selectProps(state, {}));

    const {
        outrightsCountBySport,
        categorizerItems: outrightCategorizerItems,
    } = useOutrightsCountBySport();

    const { requestContent } = usePoller<typeof getCategorizer>({
        pollerName: App.PollerName.Categorizer,
        executableAction: getCategorizer,
        skipInitialRequest: checkSkipPollerConfiguration(
            SKIP_MATCH_LIST_INITIAL_REQUEST[location.pathname as App.Routes]
        ),
        variables,
    });

    const sportType = getSportTypeFromLocation();

    const sortedCategorizerItems = useMemo(
        () =>
            getSortedCategorizerItems({
                sportType: SportType.Mix,
                sports,
                sportsOrder,
                categorizerItems,
                outrightCategorizerItems,
            }),
        [sports, sportsOrder, categorizerItems, outrightCategorizerItems]
    );

    useEffect(() => {
        requestContent();
    }, [variables, requestContent]);

    const notifyCmsAboutRedirect = useCallback(
        ({
            requestedSportType,
            sportIds,
            tournamentIds,
        }: {
            requestedSportType: App.SportType;
            sportIds: string[];
            tournamentIds: string[];
        }) => {
            const nextSearch = stringify({
                sportIds,
                tournamentIds,
            });

            const slicedPathname = pathname
                .replace(App.Routes.ESPORT, '')
                .replace(App.Routes.CLASSIC, '');

            const parentPath = getRouteBySportType(requestedSportType);

            const nextPathname =
                `${parentPath}${slicedPathname}` || App.Routes.UPCOMING_LIST;

            history.push({
                ...history.location,
                hash: window.location.hash,
                pathname: nextPathname,
                search: nextSearch,
            });
        },
        [history, pathname]
    );

    const getRequestedSportType = useCallback(
        (sportIds: string[]) => {
            if (sportType === SportType.Mix) return null;

            const nextSportType = getSportTypeBySelectedSports(
                sports as any,
                sportIds
            );

            return nextSportType && sportType !== nextSportType
                ? nextSportType
                : null;
        },
        [sports, sportType]
    );

    const handleSportSelect = useCallback(
        ({ value, children }: API.CategorizerItem): void => {
            const { sportIds = [], tournamentIds = [] } = getAppliedFilters();

            if (
                includes(OUTRIGHTS_ONLY_SPORTS, value) &&
                !checkOnRoute(App.Routes.OUTRIGHTS_LIST)
            ) {
                const historyOutrightSport = getHistory();
                historyOutrightSport.push({
                    ...historyOutrightSport.location,
                    ...getSummaryPath(value),
                });

                return;
            }

            const isSportSelected = sportIds.includes(value);
            const chosenSportTournamentIds = children.map(child => child.value);

            const nextSportIds = isSportSelected
                ? without(sportIds, value)
                : concat(sportIds, value);
            const nextTournamentIds = isSportSelected
                ? without(tournamentIds, ...chosenSportTournamentIds)
                : tournamentIds.concat(chosenSportTournamentIds);

            const requestedSportType = getRequestedSportType(nextSportIds);

            if (requestedSportType) {
                notifyCmsAboutRedirect({
                    requestedSportType,
                    sportIds: nextSportIds,
                    tournamentIds: nextTournamentIds,
                });
            }

            onFilterChange(nextSportIds, nextTournamentIds);
        },
        [getRequestedSportType, onFilterChange, notifyCmsAboutRedirect]
    );

    const handleTournamentIdsChange = useCallback(
        (nextTournamentIds: string[]) => {
            const sportsWithCheckedTournaments = getSportsWithCheckedTournaments(
                categorizerItems,
                nextTournamentIds
            );

            const nextSportIds = union(
                // Sports without any selected tournament should be deselected
                intersection(selectedSportIds, sportsWithCheckedTournaments),
                sportsWithCheckedTournaments
            );

            onFilterChange(nextSportIds, nextTournamentIds);
        },
        [selectedSportIds, categorizerItems, onFilterChange]
    );

    return {
        outrightsCountBySport,
        sortedCategorizerItems,
        handleSportSelect,
        handleTournamentIdsChange,
    };
};

function getSportTypeBySelectedSports(
    sports: ImmutableMap<string, View.Sport>,
    selectedSportIds: string[]
): App.SportType | null {
    if (!selectedSportIds.length) return null;

    return getSportTypeBySportTags(
        sports as ImmutableMap<string, View.Sport>,
        selectedSportIds
    );
}

export default useCategorizerBody;
